function dayId(eventId, index) {
  return `Event${eventId}_${index}`
}

function buildDayIdRemap(event, dayIndex) {
  const remap = {}
  const deletedId = dayId(event.id, dayIndex)
  remap[deletedId] = null
  for (let i = dayIndex + 1; i < event.days.length; i += 1) {
    remap[dayId(event.id, i)] = dayId(event.id, i - 1)
  }
  return remap
}

function buildReorderIndexMap(length, fromIndex, toIndex) {
  const order = Array.from({ length }, (_, i) => i)
  const [moved] = order.splice(fromIndex, 1)
  order.splice(toIndex, 0, moved)
  const indexMap = {}
  order.forEach((oldIndex, newIndex) => {
    indexMap[oldIndex] = newIndex
  })
  return indexMap
}

// Keep day-keyed objects in ascending day order.
// Some consumers (e.g. applying an event template) rely on Object.values order.
function sortDayKeys(obj) {
  if (!obj) return
  const entries = Object.entries(obj)
  entries.sort((a, b) => {
    const ai = parseInt(a[0].split('_').pop(), 10)
    const bi = parseInt(b[0].split('_').pop(), 10)
    if (Number.isNaN(ai) || Number.isNaN(bi)) return 0
    return ai - bi
  })
  Object.keys(obj).forEach((key) => {
    delete obj[key]
  })
  entries.forEach(([key, value]) => {
    obj[key] = value
  })
}

function remapKeyedObject(obj, remap) {
  if (!obj) return
  const keys = Object.keys(obj)
  const toMove = []
  keys.forEach((key) => {
    if (!(key in remap)) return
    const newKey = remap[key]
    const value = obj[key]
    delete obj[key]
    if (newKey != null) toMove.push([newKey, value])
  })
  // Apply higher indices first so we don't overwrite before reading
  toMove.sort((a, b) => {
    const ai = parseInt(a[0].split('_').pop(), 10)
    const bi = parseInt(b[0].split('_').pop(), 10)
    return bi - ai
  })
  toMove.forEach(([newKey, value]) => {
    obj[newKey] = value
  })
}

function remapPeopleCountByDay(event, dayIndex) {
  if (!event.people_count_by_day) return
  const byDay = event.people_count_by_day
  const next = {}
  Object.keys(byDay).forEach((key) => {
    const index = parseInt(key, 10)
    if (Number.isNaN(index)) return
    if (index < dayIndex) next[index] = byDay[key]
    else if (index > dayIndex) next[index - 1] = byDay[key]
  })
  event.people_count_by_day = next
}

function remapDayId(dayIdValue, remap) {
  if (!dayIdValue || !(dayIdValue in remap)) return dayIdValue
  return remap[dayIdValue]
}

function eventEndDate(event) {
  return event.start_date.addDays(event.days.length - 1)
}

function daysBetween(from, to) {
  return Math.round((to - from) / 86400000)
}

function isDateUsedByOtherEvent(session, event, date) {
  return session.events.some(
    (e) =>
      e !== event && daysBetween(e.start_date, date) >= 0 && daysBetween(date, eventEndDate(e)) >= 0
  )
}

async function remapOrdersAndInventories(vm, remap) {
  const sessionId = vm.$root.session.id
  const orders = Object.values(vm.$root.orders || {}).filter(
    (order) => order.session_id === sessionId
  )
  for (const order of orders) {
    const newDelivery = remapDayId(order.delivery_day, remap)
    const newTarget = remapDayId(order.target_day, remap)
    if (newDelivery !== order.delivery_day || newTarget !== order.target_day) {
      await vm.dbUpdate('orders', { ...order, delivery_day: newDelivery, target_day: newTarget })
    }
  }
  const inventories = Object.values(vm.$root.inventories || {}).filter(
    (inventory) => inventory.session_id === sessionId
  )
  for (const inventory of inventories) {
    const newDay = remapDayId(inventory.day, remap)
    if (newDay !== inventory.day) {
      await vm.dbUpdate('inventories', { ...inventory, day: newDay })
    }
  }
}

// Push following events by one day when they start on or right after the previous event end,
// so that the relative layout (adjacent / one-day overlap) is preserved
function shiftFollowingEvents(session, event, oldEndDate) {
  let chainOldEnd = oldEndDate
  const following = session.events
    .filter((e) => e !== event && e.start_date > event.start_date)
    .sort((a, b) => a.start_date - b.start_date)
  for (const next of following) {
    if (daysBetween(chainOldEnd, next.start_date) > 1) break
    const nextOldEnd = eventEndDate(next)
    next.start_date = next.start_date.addDays(1)
    if (nextOldEnd > chainOldEnd) chainOldEnd = nextOldEnd
  }
}

/**
 * Insert a new day before the given day of an event and reindex all related session data.
 * If the previous date is free (first day of the event and no other event on that date),
 * the event simply starts one day earlier. Otherwise the following days are shifted by one day.
 * @param {object} vm - Vue component instance (needs $root, dbUpdate)
 * @param {object} event - event object from session.events
 * @param {number} dayIndex - index of the day before which the new day is inserted
 */
export async function insertDayInEvent(vm, event, dayIndex, label = 'New day') {
  if (!event || !event.days || dayIndex < 0 || dayIndex >= event.days.length) return

  const session = vm.$root.session
  const previousDate = event.start_date.addDays(dayIndex - 1)
  const previousDateFree = dayIndex === 0 && !isDateUsedByOtherEvent(session, event, previousDate)
  const oldEndDate = eventEndDate(event)

  const remap = {}
  for (let i = event.days.length - 1; i >= dayIndex; i -= 1) {
    remap[dayId(event.id, i)] = dayId(event.id, i + 1)
  }
  const newDayId = dayId(event.id, dayIndex)

  // 1. Reindex schedule row values
  ;(session.rows || []).forEach((row) => {
    remapKeyedObject(row.values, remap)
    row.values[newDayId] = {}
  })

  // 2. Reindex people_count_by_day (new day copies the day it is inserted before)
  if (event.people_count_by_day) {
    const byDay = event.people_count_by_day
    const next = {}
    Object.keys(byDay).forEach((key) => {
      const index = parseInt(key, 10)
      if (Number.isNaN(index)) return
      next[index < dayIndex ? index : index + 1] = byDay[key]
    })
    next[dayIndex] = byDay[dayIndex] ?? event.people_count
    event.people_count_by_day = next
  }

  // 3. Reindex realStocks and buys
  ;[session.realStocks, session.buys].forEach((byProduct) => {
    if (!byProduct) return
    Object.values(byProduct).forEach((dayValues) => {
      remapKeyedObject(dayValues, remap)
    })
  })

  // 4. Update dates
  if (previousDateFree) event.start_date = previousDate
  else shiftFollowingEvents(session, event, oldEndDate)

  // 5. Insert the day label
  event.days.splice(dayIndex, 0, label)

  // 6. Orders and inventories follow their (reindexed) day
  await remapOrdersAndInventories(vm, remap)
}

/**
 * Remove a day from an event and reindex all related session data.
 * Orders/inventories tied to the deleted day are destroyed.
 * @param {object} vm - Vue component instance (needs $root, dbUpdate, dbDestroy)
 * @param {object} event - event object from session.events
 * @param {number} dayIndex - index of the day to remove within event.days
 */
export async function removeDayFromEvent(vm, event, dayIndex) {
  if (!event || !event.days || dayIndex < 0 || dayIndex >= event.days.length) return
  if (event.days.length <= 1) return

  const session = vm.$root.session
  const remap = buildDayIdRemap(event, dayIndex)
  const deletedDayId = dayId(event.id, dayIndex)

  // 1. Reindex schedule row values
  ;(session.rows || []).forEach((row) => {
    remapKeyedObject(row.values, remap)
  })

  // 2. Reindex people_count_by_day
  remapPeopleCountByDay(event, dayIndex)

  // 3. Reindex realStocks and buys
  ;[session.realStocks, session.buys].forEach((byProduct) => {
    if (!byProduct) return
    Object.values(byProduct).forEach((dayValues) => {
      remapKeyedObject(dayValues, remap)
    })
  })

  // 4. Orders: destroy if linked to deleted day, else remapped ids
  const sessionId = session.id
  const orders = Object.values(vm.$root.orders || {}).filter(
    (order) => order.session_id === sessionId
  )

  for (const order of orders) {
    const deliveryDeleted = order.delivery_day === deletedDayId
    const targetDeleted = order.target_day === deletedDayId
    if (deliveryDeleted || targetDeleted) {
      await vm.dbDestroy('orders', order)
      continue
    }
    const newDelivery = remapDayId(order.delivery_day, remap)
    const newTarget = remapDayId(order.target_day, remap)
    if (newDelivery !== order.delivery_day || newTarget !== order.target_day) {
      await vm.dbUpdate('orders', {
        ...order,
        delivery_day: newDelivery,
        target_day: newTarget,
      })
    }
  }

  // 5. Inventories: destroy if on deleted day, else remapped id
  const inventories = Object.values(vm.$root.inventories || {}).filter(
    (inventory) => inventory.session_id === sessionId
  )

  for (const inventory of inventories) {
    if (inventory.day === deletedDayId) {
      await vm.dbDestroy('inventories', inventory)
      continue
    }
    const newDay = remapDayId(inventory.day, remap)
    if (newDay !== inventory.day) {
      await vm.dbUpdate('inventories', {
        ...inventory,
        day: newDay,
      })
    }
  }

  // 6. Remove the day label last (triggers sessionDays watcher)
  event.days.splice(dayIndex, 1)
}

/**
 * Move a day of an event to another position inside that event and reindex all related data.
 * Intended for event templates, where columns are not tied to real dates.
 * @param {object} vm - Vue component instance (needs $root)
 * @param {object} event - event object from session.events
 * @param {number} fromIndex - current index of the day
 * @param {number} toIndex - target index of the day after the move
 */
export function reorderDaysInEvent(vm, event, fromIndex, toIndex) {
  if (!event || !event.days) return
  const length = event.days.length
  if (fromIndex === toIndex) return
  if (fromIndex < 0 || fromIndex >= length || toIndex < 0 || toIndex >= length) return

  const session = vm.$root.session
  const indexMap = buildReorderIndexMap(length, fromIndex, toIndex)
  const remap = {}
  Object.keys(indexMap).forEach((oldIndex) => {
    remap[dayId(event.id, Number(oldIndex))] = dayId(event.id, indexMap[oldIndex])
  })

  // 1. Reindex schedule row values
  ;(session.rows || []).forEach((row) => {
    remapKeyedObject(row.values, remap)
    sortDayKeys(row.values)
  })

  // 2. Reindex people_count_by_day
  if (event.people_count_by_day) {
    const byDay = event.people_count_by_day
    const next = {}
    Object.keys(byDay).forEach((key) => {
      const index = parseInt(key, 10)
      if (Number.isNaN(index) || indexMap[index] === undefined) return
      next[indexMap[index]] = byDay[key]
    })
    event.people_count_by_day = next
  }

  // 3. Reindex realStocks and buys
  ;[session.realStocks, session.buys].forEach((byProduct) => {
    if (!byProduct) return
    Object.values(byProduct).forEach((dayValues) => {
      remapKeyedObject(dayValues, remap)
      sortDayKeys(dayValues)
    })
  })

  // 4. Move the day label last (triggers sessionDays watcher)
  const [day] = event.days.splice(fromIndex, 1)
  event.days.splice(toIndex, 0, day)
}
